import { useMemo, useState } from "react";
import { Badge, Box, ClickAwayListener, IconButton, Paper, Popper, Stack, Tooltip, Typography } from "@material-ui/core";
import { Event } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { eventActivityItems, summarizeListingActivity } from "utils/listings/listingActivity";
import type { ListingActivityState } from "utils/listings/listingActivity";
import type { CalendarEvent } from "../../../../@types/sharedSchema";
import { activityText, activityWho, formatActivityWhen } from "./activityText";
import { ListingActivityList } from "./ListingActivityList";

type Props = {
  /** The listing's meetings and tasks: the user's own and her office's (tasks included). */
  events: CalendarEvent[];
};

const POPOVER_MAX_ITEMS = 8;

// Palette colours that never change, not `primary`: a user can re-colour the theme (red would read as late).
const ICON_COLOR: Record<ListingActivityState, string> = {
  overdue: "error.main",
  ahead: "info.main",
  past: "text.disabled"
};

const STATE_LABEL: Record<ListingActivityState, string> = {
  overdue: "listingActivity.overdue",
  ahead: "listingActivity.ahead",
  past: "listingActivity.past"
};

/** Teammates' names by lowercased email, for "who" on every row. */
export function useOfficeNames(): Map<string, string> {
  const { organizationUsers } = useFirestore();
  return useMemo(() => {
    const names = new Map<string, string>();
    for (const user of organizationUsers ?? []) if (user.email) names.set(user.email.toLowerCase(), user.name || user.email);
    return names;
  }, [organizationUsers]);
}

/**
 * At a glance, whether anything is happening on a listing (Trello #246): ONE calendar icon for its meetings
 * and tasks, the user's own and her office's. Blue while something is ahead, red with a dot when a task is
 * overdue (the dot says it without colour), grey when all of it is history. The tooltip names that one
 * thing, what, who and when; a click opens the whole list. Renders nothing for a listing nobody has touched.
 */
export function ListingActivityIndicator({ events }: Props) {
  const { t, i18n } = useTranslation();
  const {
    user: { email }
  } = useAuth();
  const names = useOfficeNames();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // "Now" is taken with the events: what is ahead is judged again whenever they change.
  const { now, items, summary } = useMemo(() => {
    const at = new Date();
    const eventItems = eventActivityItems(events, email, at);
    return { now: at, items: eventItems, summary: summarizeListingActivity(eventItems, at) };
  }, [events, email]);

  const { state, shown, moreAhead } = summary;
  if (!state || !shown) return null;

  const tooltip = (
    <Box sx={{ py: 0.25 }}>
      <Typography variant="caption" component="div" sx={{ opacity: 0.72 }}>
        {t(STATE_LABEL[state])}
      </Typography>
      <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
        {activityText(shown, t)}
      </Typography>
      <Typography variant="caption" component="div">
        {t(`listingActivity.kind.${shown.kind}`)} · {activityWho(shown, names, t)} · {formatActivityWhen(shown, now, t, i18n.language)}
      </Typography>
      {moreAhead > 0 && (
        <Typography variant="caption" component="div" sx={{ opacity: 0.72 }}>
          {moreAhead === 1 ? t("listingActivity.moreAheadOne") : t("listingActivity.moreAheadOther", { count: moreAhead })}
        </Typography>
      )}
    </Box>
  );

  const toggle = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchor(anchor ? null : e.currentTarget);
  };

  return (
    <>
      {/* No tooltip while the list is open: it would sit on top of it. */}
      <Tooltip title={anchor ? "" : tooltip} describeChild>
        <IconButton
          size="small"
          onClick={toggle}
          sx={{ p: 0.25 }}
          aria-label={t("listingActivity.title")}
          aria-haspopup="true"
          aria-expanded={Boolean(anchor)}
        >
          <Badge
            variant="dot"
            color="error"
            invisible={state !== "overdue"}
            sx={{
              // The theme's dot is 10px: half this icon.
              "& .MuiBadge-dot": {
                width: 7,
                height: 7,
                minWidth: 7,
                borderRadius: "50%",
                // Ringed in the paper colour, so it reads apart from the red icon under it.
                boxShadow: (theme) => `0 0 0 1.5px ${theme.palette.background.paper}`
              }
            }}
          >
            <Event sx={{ fontSize: 16, color: ICON_COLOR[state] }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popper open={Boolean(anchor)} anchorEl={anchor} placement="bottom-start" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setAnchor(null)}>
          <Paper
            elevation={8}
            sx={{ width: { xs: "calc(100vw - 32px)", sm: 340 }, maxWidth: 360, maxHeight: 380, overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack sx={{ p: 1.5 }} spacing={0.5}>
              <Typography variant="subtitle2">{t("listingActivity.title")}</Typography>
              <ListingActivityList items={items} names={names} now={now} maxItems={POPOVER_MAX_ITEMS} />
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
